import { useEffect, useState } from "react";

import { Reply } from '@/types'
import { RepliesGroup } from "./RepliesGroup";
import AppBox from "../AppBox";
import AfterHoursButton from "../AfterHoursButton";
import { useAppSelector } from "@/store/hooks";

interface MessageLeadRepliesProps {
  replies: Reply[];
  onChange: (updatedReplies: Reply[]) => void;
}

const MessageLeadReplies: React.FC<MessageLeadRepliesProps> = ({ replies, onChange }) => {
  const [form, setForm] = useState<Record<number, Partial<Reply>>>({});
  const [noPhoneActive, setNoPhoneActive] = useState(false);

  const { me } = useAppSelector((state) => state.auth);
  const afterHoursEnabled = me?.after_hours_enabled ?? false;

  useEffect(() => {
    const initialForm: Record<number, Partial<Reply>> = {};
    replies.forEach(reply => {
      initialForm[reply.id] = {
        text: reply.text,
        delay: reply.delay,
        active: reply.active,
      };
    });
    setForm(initialForm);

    const anyNoPhoneActive = replies.some(r => r.no_phone && r.active);
    setNoPhoneActive(anyNoPhoneActive);
  }, [replies]);

  const handleChange = (id: number, field: keyof Reply, value: any) => {
    const updated = replies.map(reply =>
      reply.id === id ? { ...reply, [field]: value } : reply
    );
    setForm(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
    onChange(updated);
  };

  const handleToggleActive = (id: number) => {
    const updated = replies.map(reply =>
      reply.id === id ? { ...reply, active: !reply.active } : reply
    );
    setForm(prev => ({
      ...prev,
      [id]: { ...prev[id], active: !prev[id]?.active },
    }));
    onChange(updated);
  };

  const handleToggleNoPhone = (active: boolean) => {
    setNoPhoneActive(active);
    const updated = replies.map(reply =>
      reply.no_phone ? { ...reply, active } : reply
    );
    onChange(updated);
  };

  const withPhone = replies.filter(r => r.type === "request" && !r.no_phone);
  const withoutPhone = replies.filter(r => r.type === "request" && r.no_phone);
  const afterHours = replies.filter(r => r.type === "request" && r.is_after_hours);

  return (
    <AppBox column gap={12}>
      <RepliesGroup
        title="Message Has Lead's Number"
        description="If the lead enters their number into LSA, this is the wording that will be sent. If the number is not entered, you can enable different wording on the bottom of the page"
        replies={withPhone}
        form={form}
        onChange={handleChange}
        onToggleActive={handleToggleActive}
      />
      <AppBox className="border-t border-gray-400" />
      <RepliesGroup
        title="Message Does Not Have Lead's Number"
        description="If the lead does not put their number in, you can set up different responses below"
        replies={withoutPhone}
        form={form}
        onChange={handleChange}
        onToggleActive={handleToggleActive}
        isNoPhone
        noPhoneActive={noPhoneActive}
        onToggleNoPhone={handleToggleNoPhone}
      />
      
      <AppBox className="border-t border-gray-400" />
      <RepliesGroup
        title="After Hours Message"
        replies={afterHours}
        form={form}
        onChange={handleChange}
        onToggleActive={handleToggleActive}
        disabled={!afterHoursEnabled}
        buttonSlot={afterHoursEnabled ? <AfterHoursButton /> : undefined}
        isAfterHours={true}
      />
    </AppBox>
  );
};

export default MessageLeadReplies;